import {
  CIMSPage,
  FDCConcurrenceLevelSideEffect,
  Permission,
  alphabeticalCompare,
} from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  ProjectFdcConcurrenceLevelsQuery,
  useCreateFdcConcurrenceLevelMutation,
  useEditFdcConcurrenceLevelMutation,
  useProjectFdcConcurrenceLevelsQuery,
  useRestoreFdcConcurrenceLevelMutation,
  useSoftRemoveFdcConcurrenceLevelMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import CIMSTable from 'components/CIMSTable'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { CIMSTags } from 'components/CIMSTags'
import { useOptionalDescriptionColumn } from 'components/CIMSTable/common/hooks/useDescriptionColumn'
import { useNameColumn } from 'components/CIMSTable/common/hooks/useNameColumn'
import {
  simpleCreateNotification,
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import CreateEditModalBody from './components/CreateEditModalBody'
import { Tooltip } from 'antd'
import { HelpMessage } from 'utils/constants'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { CreateEditModalBodyValues } from './components/CreateEditModalBody/types'
import { errorHandler } from 'hooks/errorHandler'
const Help = HelpMessage.FieldDesignChanges.Concurrences.Levels

const Levels: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectFdcConcurrenceLevelsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      globalId: projectId!,
      input: { projectId: { equals: projectId } },
    },
  })
  const [create] = useCreateFdcConcurrenceLevelMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createFDCConcurrenceLevel: {
        concurrenceLevel: { name },
      },
    }) => simpleCreateNotification(name),
  })
  const [edit] = useEditFdcConcurrenceLevelMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editFDCConcurrenceLevel: {
        concurrenceLevel: { name },
      },
    }) => simpleEditNotification(name),
  })
  const [remove, { loading: removing }] =
    useSoftRemoveFdcConcurrenceLevelMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        softRemoveFDCConcurrenceLevel: {
          concurrenceLevel: { name },
        },
      }) => simpleRemoveNotification(name),
    })
  const [restore, { loading: restoring }] =
    useRestoreFdcConcurrenceLevelMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        restoreFDCConcurrenceLevel: {
          concurrenceLevel: { name },
        },
      }) => simpleRestoreNotification(name),
    })
  const approvalGroups = useOptions({
    items: data?.project.fdcApprovalGroups,
    label: 'name',
    value: 'id',
    tooltip: 'description',
    sort: 'name',
  })
  const columns: CIMSColumn<
    ProjectFdcConcurrenceLevelsQuery['project']['fdcConcurrenceLevels'][0]
  >[] = [
    useNameColumn({
      help: Help.Name,
      sort: { default: 'ascend' },
    }),
    useOptionalDescriptionColumn('Concurrence Level'),
    {
      title: 'Approval Groups',
      link: CIMSPage.FDCApprovalGroups,
      width: 300,
      help: Help.ApprovalGroups,
      render: ({ approvalGroups }) => (
        <CIMSTags
          tags={approvalGroups.map(({ name, description }) => ({
            text: name,
            tooltip: description,
          }))}
        />
      ),
      filter: {
        with: 'callback',
        items: (records) =>
          records
            .flatMap(({ approvalGroups }) => approvalGroups)
            .map(({ id, name, description }) => ({
              text: name,
              value: id,
              tooltip: description,
            })),
        onFilter: (value, { approvalGroups }) =>
          approvalGroups.some(({ id }) => id === value),
      },
    },
    {
      title: 'Side Effect',
      width: 175,
      help: Help.SideEffect,
      render: ({ sideEffectId }) => {
        if (!sideEffectId) return null
        const { name, description } =
          FDCConcurrenceLevelSideEffect.fromIdOrFail(sideEffectId)
        return <Tooltip title={description}>{name}</Tooltip>
      },
      filter: {
        with: 'callback',
        items: FDCConcurrenceLevelSideEffect.values.map(
          ({ id, name, description }) => ({
            text: name,
            value: id,
            tooltip: description,
          })
        ),
        onFilter: (value, { sideEffectId }) => value === sideEffectId,
      },
      sort: {
        with: 'callback',
        onCompare: (a, b) => {
          const A = FDCConcurrenceLevelSideEffect.fromId(a.sideEffectId)
          const B = FDCConcurrenceLevelSideEffect.fromId(b.sideEffectId)
          return alphabeticalCompare(A, B, 'name')
        },
      },
    },
  ]
  return (
    <CIMSTable
      columns={columns}
      rowKey="id"
      records={data?.project.fdcConcurrenceLevels}
      loading={fetching || removing || restoring}
      actions={{
        header: {
          onReload: refetch,
          create: {
            entity: 'Concurrence Level',
            permission: Permission.CreateFDCConcurrenceLevel,
            loading: fetching,
            modal: {
              onOk: async ({
                name,
                description,
                sideEffectId,
                approvalGroupIds,
              }: CreateEditModalBodyValues) => {
                if (!projectId) return errorHandler('projectId is not defined')
                await create({
                  variables: {
                    createFdcConcurrenceLevelInput: {
                      name,
                      description,
                      projectId,
                      sideEffectId,
                      approvalGroupIds,
                    },
                  },
                })
                refetch()
              },
              body: <CreateEditModalBody approvalGroups={approvalGroups} />,
            },
          },
        },
        row: {
          remove: {
            permission: Permission.RemoveRestoreFDCConcurrenceLevel,
            onRemove: async ({ id }) => {
              await remove({ variables: { id } })
              refetch()
            },
            disabled: ({ fieldDesignChanges }) =>
              !!fieldDesignChanges.length
                ? {
                    message:
                      'Levels cannot be removed if they are associated with one or more Field Design Changes.',
                  }
                : false,
          },
          restore: {
            permission: Permission.RemoveRestoreFDCConcurrenceLevel,
            onRestore: async ({ id }) => {
              await restore({ variables: { id } })
              refetch()
            },
            metadata: ({ deleted, deletedDate, deletedUser }) => ({
              removed: deleted,
              removedBy: deletedUser,
              removedDate: deletedDate,
            }),
            default: 'active',
          },
          edit: {
            permission: Permission.EditFDCConcurrenceLevel,
            modal: {
              initialValues: ({
                name,
                description,
                sideEffectId,
                approvalGroups,
              }) => ({
                name,
                description,
                sideEffectId,
                approvalGroupIds: approvalGroups.map(({ id }) => id),
              }),
              recordName: 'name',
              body: <CreateEditModalBody approvalGroups={approvalGroups} />,
              onOk: async (
                { id },
                { approvalGroupIds, description, name, sideEffectId }
              ) => {
                await edit({
                  variables: {
                    editFdcConcurrenceLevelInput: {
                      id,
                      approvalGroupIds,
                      description,
                      name,
                      sideEffectId,
                    },
                  },
                })
                refetch()
              },
            },
          },
        },
      }}
      expand={{
        expandable: ({ fieldDesignChanges }) => !!fieldDesignChanges.length,
        render: ({ fieldDesignChanges, id }) => ({
          additionalInfo: [
            {
              type: 'deepLinks',
              label: 'Associated FDCs',
              showMore: { key: id },
              help: 'Field Design Changes associated with this Level',
              links: fieldDesignChanges.map(
                ({ formattedNumber, id: recordId, deleted }) => ({
                  page: CIMSPage.FDCTracker,
                  label: formattedNumber,
                  deep: { recordId, deleted },
                })
              ),
            },
          ],
        }),
      }}
    />
  )
}

export default Levels
